
export interface User {
  id: string;
  email: string;
  password: string;
  role: 'admin' | 'nutri';
  recoveryEmail?: string;
  isPasswordReset?: boolean;
}

export interface Nutritionist {
  title: string;
  name: string;
  crn: string;
  whatsapp: string; // Novo campo obrigatório para agendamento
}

export enum Gender {
  Male = 'Masculino',
  Female = 'Feminino',
  Other = 'Outro'
}

export interface Patient {
  id: number;
  userId: string;
  name: string;
  email?: string;
  phone: string;
  birthDate: string;
  gender: Gender;
  createdAt: string;
  anthropometricAssessments: AnthropometricAssessment[];
  mealPlans: MealPlan[];
}

export interface AnthropometricAssessment {
  id: number;
  date: string;
  weightKg: number;
  heightCm: number;
  skinfolds: {
    triceps: number;
    subscapular: number;
    suprailiac: number;
    abdominal: number;
    thigh: number;
    chest: number;
    axillary: number;
  };
  circumferences: {
    waist: number;
    hip: number;
    arm: number;
    thigh: number;
  };
  bodyFatPercentage?: number;
  leanMassKg?: number;
  fatMassKg?: number;
  bmr?: number;
  tee?: number;
}

export interface MealPlan {
  id: number;
  name: string;
  createdAt: string;
  targetCalories: number;
  meals: Meal[];
}

export interface Meal {
  id: number;
  name: string;
  items: FoodItem[];
}

export interface FoodItem {
  id: string;
  name: string;
  quantity: number;
  unit: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

export interface PlanTemplate {
  id: number;
  userId: string;
  name: string;
  sessions: number;
  durationMonths: number;
  value: number;
}

export interface AnnualGoal {
  id: number;
  userId: string;
  year: number;
  value: number;
}

export interface Plan {
  id: number;
  userId: string;
  patientId: number;
  type: string;
  value: number;
  operatorValue?: number;
  startDate: string;
  endDate: string;
  sessions: number;
  status: 'Ativo' | 'Inativo' | 'Finalizado';
  payment: 'Pago' | 'Pendente' | 'Parcial';
  paymentMethod: string;
  modality: string;
  consultations: { id: number; completed: boolean; date: string; appointmentId: number | null }[];
  partialAmountPaid?: number;
  remainingAmount?: number;
  netAmountReceived?: number;
}

export interface Appointment {
  id: number;
  userId: string;
  patientId: number;
  date: string;
  time: string;
  type: string;
  modality: string;
  locationId?: number | null;
  status: 'Agendado' | 'Confirmado' | 'Realizado' | 'Cancelado' | 'Faltou';
  notes?: string;
  linkedPlanId?: number | null;
  value: number;
}

export interface CashEntry {
  id: number;
  userId: string;
  date: string;
  description: string;
  value: number;
  type: 'entrada' | 'saida';
  category: string;
  paymentMethod: string;
  notes?: string;
  referenceId?: number; 
  referenceType?: 'plan' | 'appointment';
  groupId?: number;
}

export type AutoReminderType = 'proximity' | 'instructions' | 'return' | 'feedback' | 'birthday';

export interface Reminder {
  id: string | number;
  userId: string;
  patientId?: number;
  priority: 'Pendência' | 'Pendência Urgente';
  text: string;
  date: string;
  time?: string;
  isCompleted: boolean;
  systemGenerated?: boolean;
  autoType?: AutoReminderType;
  sourceId?: number; 
}

export interface MessageTemplate {
  id: number;
  userId: string;
  name: string;
  text: string;
}

export interface ConsultationLocation {
  id: number;
  name: string;
  address: string;
}

export interface ConsultationSettings {
  userId: string;
  onlineEnabled: boolean;
  presentialEnabled: boolean;
  locations: ConsultationLocation[];
  consultationDuration: number;
  enableProximityReminder: boolean;
  enableInstructionsReminder: boolean;
  enableReturnReminder: boolean;
  enableFeedbackReminder: boolean;
  enableBirthdayReminder: boolean;
  remindBeforeAppointmentDays: number;
  proximityTemplateId: number | null;
  remindWithInstructionsDays: number;
  instructionsTemplateId: number | null;
  remindReturnAfterDays: number;
  returnTemplateId: number | null;
  requestFeedbackAfterDays: number;
  feedbackTemplateId: number | null;
  birthdayTemplateId: number | null;
  defaultReminderTemplateId: number | null;
}

export interface SubTask {
  id: string;
  title: string;
  deadline?: string;
  isCompleted: boolean;
}

export interface Task {
  id: number;
  userId: string;
  title: string;
  description: string;
  status: 'todo' | 'doing' | 'done';
  category: string;
  deadline?: string;
  subTasks: SubTask[];
  createdAt: string;
}

export interface FeedbackPlan {
  id: string;
  name: string;
  durationMonths: number;
  standardMessage: string;
}

export interface FeedbackSettings {
  userId: string;
  nutritionistName: string;
  sendDay: string;
  plans: FeedbackPlan[];
}

export interface FeedbackStatus {
  patientId: number;
  lastSentDate: string;
}
